import { cx } from 'class-variance-authority';
import { forwardRef, ReactNode, useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import * as styles from './styles';
import type { StyleProps } from './styles';
import {
  alt,
  fromNullable,
  fromPredicate,
  getOrElseW,
  map,
} from 'fp-ts/lib/Option';
import { Typography } from '../Typography';

type ButtonTypeMap = BaseTypeMap<
  'button',
  Omit<StyleProps, 'icon'> & {
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    iconSize?: 'sm' | 'md' | 'lg';
  }
>;

export type ButtonProps = ComponentProps<ButtonTypeMap>;

export const Button: Component<ButtonTypeMap> = forwardRef(function Button(
  {
    className,
    children,
    intent,
    color,
    startIcon,
    endIcon,
    iconSize = 'md',
    type = 'button',
    padding,
    isLoading,
    inverted,
    ...rest
  },
  ref
) {
  const icon = useMemo(
    () =>
      pipe(
        children,
        fromPredicate((x) => !x),
        map(() => 'isolated'),
        alt(() =>
          pipe(
            startIcon,
            fromNullable,
            map(() => 'start')
          )
        ),
        alt(() =>
          pipe(
            endIcon,
            fromNullable,
            map(() => 'end')
          )
        ),
        getOrElseW(() => undefined)
      ) as StyleProps['icon'],
    [startIcon, endIcon, children]
  );

  const iconMargin = useMemo(
    () =>
      pipe(
        icon,
        fromPredicate((x) => x === 'start'),
        map(() => 'mr-2'),
        alt(() =>
          pipe(
            icon,
            fromPredicate((x) => x === 'end'),
            map(() => 'ml-2')
          )
        ),
        getOrElseW(() => undefined)
      ),
    [icon]
  );

  return (
    <Box
      as="button"
      {...rest}
      ref={ref}
      type={type}
      className={cx(
        styles.btn({ intent, isLoading, color, icon, padding, inverted }),
        className
      )}
    >
      {startIcon ? (
        <Box
          as="span"
          className={cx(styles.icon({ size: iconSize }), iconMargin)}
        >
          {startIcon}
        </Box>
      ) : null}
      {children ? (
        <Typography intent="button" as="span">
          {children}
        </Typography>
      ) : null}
      {endIcon ? (
        <Box
          as="span"
          className={cx(styles.icon({ size: iconSize }), iconMargin)}
        >
          {endIcon}
        </Box>
      ) : null}
    </Box>
  );
});
